import { Box, CircularProgress, Typography, Link } from '@mui/material'
import React, { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { ROUTES } from '../helpers/routes-helper'
import { openInstagram } from '../helpers/instagram-helper'
import {
  CONTACT_URL,
  COOKIE_POLICY_URL,
  COPY_RIGHT_COMPANY,
  COPY_RIGHT_PREFIX,
  COPY_RIGHT_SUFFIX,
  FACEBOOK_PAGE_URL,
  MOBILE_APP_HEIGHT,
  OKAO_LABS_URL,
  PRIVACY_POLICY_URL,
  TERMS_AND_CONDITION_URL,
} from '../helpers/static-constants'
import useAppUtilities from '../hooks/useAppUtilities'
import { useDevice } from '../hooks/useDevice'
import {
  MuiAlert,
  MuiBackdrop,
  MuiSnackbar,
} from '../pages/eater/styled/global.styled'
import { useAppSelector } from '../store'
import {
  selectIsLoading,
  selectMessage,
  selectSeverity,
  selectShouldOpen,
  setOpen,
} from '../store/authSlice'
import { logEvent } from '../analytics/analytics'
import ChatGptInstallModal from './ChatGptInstallModal'
import FooterDisclaimerModel from './FooterDisclaimerModel'
import {
  FooterDisclaimerTypography,
  FooterLink,
  FooterLinkMainBox,
  FooterMainBox,
  FooterSecondBox,
  FooterSeparator,
  FooterSocialIconBox,
  LinkBox,
} from './styled/Footer.styled'
import SubscribeForm from './SubscribeForm'
import ImgIcon from './ui/ImgIcon'

const Footer: React.FC = () => {
  const { theme, dispatch, isMediumScreen } = useAppUtilities()
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const isLoading = useAppSelector(selectIsLoading)
  const openServer = useAppSelector(selectShouldOpen)
  const { isHybrid } = useDevice()

  const [openDisclaimerModel, setOpenDisclaimerModel] = useState(false)
  const [openChatGptInstall, setOpenChatGptInstall] = useState(false)

  const handleChatGptInstallOpen = (): void => {
    logEvent('chatgpt_install_opened', { surface: 'footer' })
    setOpenChatGptInstall(true)
  }

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  return (
    <>
      <FooterMainBox>
        <FooterSecondBox>
          <FooterLinkMainBox>
            <LinkBox>
              <Link
                sx={{ color: 'common.white', textDecoration: 'underline' }}
                component={RouterLink}
                to={ROUTES.ABOUT}
              >
                About
              </Link>
              <Box
                onClick={() => setOpenDisclaimerModel(true)}
                sx={{ cursor: 'pointer' }}
              >
                <FooterDisclaimerTypography>
                  Disclaimer
                </FooterDisclaimerTypography>
              </Box>
              {!isHybrid && (
                <Box
                  onClick={handleChatGptInstallOpen}
                  sx={{ cursor: 'pointer' }}
                >
                  <FooterDisclaimerTypography>
                    Use Resactly in ChatGPT
                  </FooterDisclaimerTypography>
                </Box>
              )}
              <FooterLink href={CONTACT_URL}>Contact</FooterLink>
              <FooterLink href={COOKIE_POLICY_URL}>Cookie Policy</FooterLink>
              <FooterLink href={PRIVACY_POLICY_URL}>Privacy Policy</FooterLink>
              <FooterLink href={TERMS_AND_CONDITION_URL}>
                Terms and Conditions
              </FooterLink>
            </LinkBox>
            <Typography variant="subtitle1" color={theme.palette.grey[400]}>
              {COPY_RIGHT_PREFIX}
              <Link
                href={OKAO_LABS_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'inherit', textDecoration: 'underline' }}
              >
                {COPY_RIGHT_COMPANY}
              </Link>
              {COPY_RIGHT_SUFFIX}
            </Typography>
          </FooterLinkMainBox>
          <SubscribeForm />
          <FooterSeparator />
          <FooterSocialIconBox>
            <Box
              display="flex"
              pb={isHybrid ? `${MOBILE_APP_HEIGHT - 10}px` : '0px'}
            >
              <ImgIcon
                icon="/new/images/icons/facebook.svg"
                alt="facebook"
                wrapperStyle={{
                  backgroundColor: theme.palette.primary.light,
                  marginRight: isMediumScreen ? '16px' : '8px',
                }}
                onClick={() => window.open(FACEBOOK_PAGE_URL, '_target')}
              />
              <ImgIcon
                icon="/new/images/icons/instagram.svg"
                alt="instagram"
                wrapperStyle={{ backgroundColor: theme.palette.primary.light }}
                onClick={() => {
                  void openInstagram()
                }}
              />
            </Box>
          </FooterSocialIconBox>
          <MuiBackdrop open={isLoading}>
            <CircularProgress color="inherit" />
          </MuiBackdrop>
        </FooterSecondBox>
        <MuiSnackbar open={openServer} onClose={setOpenServer}>
          <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
            {message}
          </MuiAlert>
        </MuiSnackbar>
      </FooterMainBox>
      <FooterDisclaimerModel
        open={openDisclaimerModel}
        handleClose={() => setOpenDisclaimerModel(false)}
      />
      {!isHybrid && (
        <ChatGptInstallModal
          open={openChatGptInstall}
          handleClose={() => setOpenChatGptInstall(false)}
        />
      )}
    </>
  )
}

export default Footer
